import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/database';
import router from '@/router';
import {
  buildSessionUser,
  decodeSessionUser,
  encodeSessionUser,
  getByUser,
  getCompany,
  sanitizeSessionUser,
  setSessionUserData,
} from './cacheService';
import {
  ACCOUNT_SWITCHER_STORAGE_KEY,
  clearStoredAccountSwitcher,
  getAccountDataKey,
  normalizeAccountEmail,
  removeStoredAccountSwitcherAccount,
} from './logoutStorage';
import { switchLoginAccount } from './loginPin';

/**
 * Account Switcher Store Module
 * 
 * Enables multi-account switching for users with "+" email addresses
 * (e.g., tareef+acme@happily.ai, tareef+bigcorp@happily.ai)
 * 
 * Similar to Slack's workspace switcher, this allows partners and
 * Customer Success reps to quickly switch between customer accounts.
 */

export interface AccountInfo {
  email: string;           // Full email with + (e.g., tareef+acme@happily.ai)
  company: string;         // Company key
  companyName: string;     // Display name (e.g., "Acme Corp")
  initials: string;        // Two-letter initials for avatar
  color: string;           // Background color for avatar
  lastAccess: number;      // Timestamp of last access
  isSSO: boolean;          // Whether this account uses SSO (requires re-auth)
  isAdviser?: boolean;     // Whether this account is from users_adviser
}

export interface AccountSwitcherState {
  baseEmail: string;              // Base email without + (e.g., tareef@happily.ai)
  accounts: AccountInfo[];        // All cached accounts
  activeEmail: string;            // Currently active account email
  isMultiAccountUser: boolean;    // Whether user has + email pattern
  isSwitching: boolean;           // Loading state during switch
  switchingToCompany: string;     // Company name being switched to (for overlay)
}

// Color palette for account avatars (excluding similar colors)
const AVATAR_COLORS = [
  '#6C30CC', // Purple (primary brand)
  '#32C2D5', // Cyan (secondary brand)
  '#E91E63', // Pink
  '#FF5722', // Deep Orange
  '#4CAF50', // Green
  '#2196F3', // Blue
  '#FFC107', // Amber
  '#795548', // Brown
  '#607D8B', // Blue Grey
  '#9C27B0', // Purple variant
];

const STORAGE_KEY = ACCOUNT_SWITCHER_STORAGE_KEY;

function readCachedAccountData(email: string): any {
  try {
    return decodeSessionUser(localStorage.getItem(getAccountDataKey(email)));
  } catch (error) {
    console.warn('[accountSwitcher] failed to read cached account data:', error);
    return null;
  }
}

function emailToKey(email: string): string {
  return String(email || '').toLowerCase().trim().replace(/\./g, '||');
}

export default {
  namespaced: true,
  
  state: (): AccountSwitcherState => ({
    baseEmail: '',
    accounts: [],
    activeEmail: '',
    isMultiAccountUser: false,
    isSwitching: false,
    switchingToCompany: '',
  }),

  mutations: {
    setBaseEmail(state: AccountSwitcherState, email: string) {
      state.baseEmail = email;
    },
    
    setAccounts(state: AccountSwitcherState, accounts: AccountInfo[]) {
      state.accounts = accounts;
    },
    
    setActiveEmail(state: AccountSwitcherState, email: string) {
      state.activeEmail = email;
    },
    
    setIsMultiAccountUser(state: AccountSwitcherState, value: boolean) {
      state.isMultiAccountUser = value;
    },
    
    setIsSwitching(state: AccountSwitcherState, value: boolean) {
      state.isSwitching = value;
    },
    
    setSwitchingToCompany(state: AccountSwitcherState, value: string) {
      state.switchingToCompany = value;
    },
    
    addAccount(state: AccountSwitcherState, account: AccountInfo) {
      // Remove existing entry for this email if present
      state.accounts = state.accounts.filter(a => a.email !== account.email);
      // Add new/updated account
      state.accounts.push(account);
    },
    
    removeAccount(state: AccountSwitcherState, email: string) {
      state.accounts = state.accounts.filter(a => a.email !== email);
    },
    
    updateLastAccess(state: AccountSwitcherState, email: string) {
      const account = state.accounts.find(a => a.email === email);
      if (account) {
        account.lastAccess = Date.now();
      }
    },
  },

  actions: {
    /**
     * Initialize account switcher from localStorage
     * Called on app startup
     */
    initialize({ commit, dispatch }: any) {
      try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
          const data = JSON.parse(stored);
          const accounts = data.accounts || [];

          // Migration: move embedded cachedUserData to individual localStorage keys
          let needsRePersist = false;
          for (const account of accounts) {
            if ((account as any).cachedUserData) {
              localStorage.setItem(
                getAccountDataKey(account.email),
                (account as any).cachedUserData
              );
              delete (account as any).cachedUserData;
              needsRePersist = true;
            }

            if ((account as any).isAdviser === undefined) {
              const cachedUser = readCachedAccountData(account.email);
              if (cachedUser && cachedUser.is_adviser !== undefined) {
                (account as any).isAdviser = !!cachedUser.is_adviser;
                needsRePersist = true;
              }
            }
          }

          commit('setBaseEmail', data.baseEmail || '');
          commit('setAccounts', accounts);
          commit('setActiveEmail', data.activeEmail || '');
          commit('setIsMultiAccountUser', accounts.length > 0);

          if (needsRePersist) {
            dispatch('persist');
          }
        }
      } catch (error) {
        console.warn('Failed to load account switcher data:', error);
        dispatch('clear');
      }
    },

    /**
     * Save current state to localStorage
     */
    persist({ state }: any) {
      try {
        const data = {
          baseEmail: state.baseEmail,
          accounts: state.accounts,
          activeEmail: state.activeEmail,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch (error) {
        console.warn('Failed to persist account switcher data:', error);
      }
    },

    /**
     * Clear all account switcher data
     */
    clear({ commit }: any) {
      clearStoredAccountSwitcher();
      commit('setBaseEmail', '');
      commit('setAccounts', []);
      commit('setActiveEmail', '');
      commit('setIsMultiAccountUser', false);
    },

    /**
     * Register a new account after successful login
     * Called from authen.ts after PIN verification succeeds
     */
    registerAccount({ commit, dispatch, state, getters }: any, payload: {
      email: string;
      userData: any;  // The user object that gets stored in localStorage.userData
      isSSO?: boolean;
    }) {
      const { email, userData, isSSO = false } = payload;
      
      // Check if this is a plus-email
      if (!getters.isPlusEmail(email)) {
        return; // Not a multi-account user, skip
      }
      
      // Extract base email
      const baseEmail = getters.extractBaseEmail(email);
      
      // If this is first account or same base email
      if (!state.baseEmail || state.baseEmail === baseEmail) {
        commit('setBaseEmail', baseEmail);
        commit('setIsMultiAccountUser', true);

        // Generate initials and color for this company
        const initials = getters.generateInitials(userData.company_name);
        const color = getters.getColorForEmail(email);

        // Store cached user data in its own localStorage key (keeps main array lightweight)
        const encodedData = encodeSessionUser(sanitizeSessionUser(userData));
        localStorage.setItem(getAccountDataKey(email), encodedData);

        // Create account info (no cachedUserData embedded)
        const accountInfo: AccountInfo = {
          email: email.toLowerCase(),
          company: userData.company,
          companyName: userData.company_name,
          initials,
          color,
          lastAccess: Date.now(),
          isSSO,
          isAdviser: !!userData.is_adviser,
        };

        commit('addAccount', accountInfo);
        commit('setActiveEmail', email.toLowerCase());

        // Persist to localStorage
        dispatch('persist');
      }
    },

    /**
     * Switch to a different account
     * This routes the user through the normal PIN/SSO login flow.
     */
    async switchAccount({ commit, dispatch, state }: any, email: string) {
      const account = state.accounts.find((a: AccountInfo) => a.email === email);
      
      if (!account) {
        console.error('Account not found:', email);
        return;
      }
      
      // If it's the active account, do nothing
      if (email === state.activeEmail) {
        return;
      }

      const cachedUser = readCachedAccountData(email);
      const isAdviser = !!(account.isAdviser || (cachedUser && cachedUser.is_adviser));

      if (isAdviser) {
        commit('setIsSwitching', true);
        commit('setSwitchingToCompany', account.companyName);
        localStorage.setItem('accountSwitchInProgress', JSON.stringify({
          companyName: account.companyName,
          email,
        }));

        try {
          const switchResult = await switchLoginAccount(email);
          if (!switchResult.ok) {
            commit('setIsSwitching', false);
            commit('setSwitchingToCompany', '');
            localStorage.removeItem('accountSwitchInProgress');

            if (switchResult.requiresPin) {
              return {
                requiresPin: true,
                email,
                message: switchResult.message,
              };
            }

            throw new Error(switchResult.message || 'Unable to switch account. Please try again.');
          }

          await firebase.auth().signInWithCustomToken(switchResult.customToken || '');
          const targetEmail = String(switchResult.email || email).toLowerCase();
          let sessionUser = readCachedAccountData(targetEmail);

          if (!sessionUser || !sessionUser.is_adviser || sessionUser.company !== switchResult.company) {
            let user:any = null;
            try {
              user = await getByUser(targetEmail);
            } catch (error) {
              // getByUser can miss adviser records; use cached/switch metadata below.
            }

            if (!user) {
              try {
                const adviserSnapshot = await firebase.database().ref(`/users_adviser/${emailToKey(targetEmail)}`).once('value');
                user = adviserSnapshot.val();
              } catch (error) {
                console.warn('[accountSwitcher] users_adviser lookup failed during switch:', error);
              }
            }

            if (!user) {
              const fallbackCompany = switchResult.company || account.company;
              if (targetEmail && fallbackCompany) {
                user = {
                  ...(cachedUser || {}),
                  email: targetEmail,
                  company: fallbackCompany,
                  user_type: switchResult.user_type || (cachedUser && cachedUser.user_type) || 'hr',
                  firstname: (cachedUser && cachedUser.firstname) || account.companyName || '',
                  lastname: (cachedUser && cachedUser.lastname) || '',
                  is_adviser: true,
                };
              }
            }

            if (!user) {
              throw new Error('User not found. Please verify this advisor account again.');
            }

            let company:any = (cachedUser && cachedUser.companyData && cachedUser.company === user.company)
              ? cachedUser.companyData
              : null;
            try {
              company = await getCompany(user.company);
            } catch (error) {
              try {
                const companySnapshot = await firebase.database().ref(`/company/${user.company}`).once('value');
                company = companySnapshot.val();
              } catch (rtdbError) {
                console.warn('[accountSwitcher] company lookup failed during switch:', rtdbError);
              }
            }

            if (!company && cachedUser && cachedUser.companyData) {
              company = cachedUser.companyData;
            }

            sessionUser = buildSessionUser(user, company || { name: account.companyName, key: user.company }, true);
          }

          if (!setSessionUserData(sessionUser)) {
            throw new Error('Browser storage is full. Clear site data for this page and try again.');
          }

          const versionSnapshot = await firebase.database().ref('version/my_version').once('value');
          localStorage.setItem('userVersion', String(versionSnapshot.val() || ''));

          commit('authen/setUserLogin', sessionUser, { root: true });
          commit('authen/setCompanyData', {
            ...(sessionUser.companyData || {}),
            key: sessionUser.company,
          }, { root: true });

          dispatch('registerAccount', {
            email: sessionUser.email,
            userData: sessionUser,
            isSSO: false,
          });
          commit('updateLastAccess', targetEmail);
          commit('setActiveEmail', targetEmail);
          dispatch('persist');

          localStorage.removeItem('addingAccount');
          localStorage.removeItem('accountSwitchInProgress');
          commit('setIsSwitching', false);
          commit('setSwitchingToCompany', '');
          window.location.reload();
          return { switched: true };
        } catch (error) {
          console.error('Failed to switch adviser account:', error);
          commit('setIsSwitching', false);
          commit('setSwitchingToCompany', '');
          localStorage.removeItem('accountSwitchInProgress');
          throw error;
        }
      }
      
      try {
        // For SSO accounts, redirect to login flow
        if (account.isSSO) {
          // Clear current session but keep account data
          await firebase.auth().signOut();
          localStorage.removeItem('userData');
          
          // Store the target email to resume after SSO
          localStorage.setItem('switchToEmail', email);
          
          // Redirect to login
          router.replace('/login');
          return;
        }
        
        await firebase.auth().signOut();
        localStorage.removeItem('userData');
        localStorage.setItem('switchToEmail', email);
        router.replace('/login');
        
      } catch (error) {
        console.error('Failed to switch account:', error);
        commit('setIsSwitching', false);

        // If auth fails, the cached data might be stale
        // Remove this account and prompt re-login
        localStorage.removeItem(getAccountDataKey(email));
        commit('removeAccount', email);
        dispatch('persist');

        // Show error message
        throw new Error('Failed to switch account. Please log in again.');
      }
    },

    /**
     * Remove an account from the switcher
     */
    removeAccount({ commit, dispatch, state }: any, email: string) {
      const normalizedEmail = normalizeAccountEmail(email);
      // Clean up individual cached data key
      removeStoredAccountSwitcherAccount(normalizedEmail);
      commit('removeAccount', normalizedEmail);
      dispatch('persist');

      // If no accounts left, clear multi-account mode
      if (state.accounts.length === 0) {
        dispatch('clear');
      }
    },

    /**
     * Handle logout - keep account in list but clear active state
     */
    handleLogout({ commit, dispatch, state }: any, payload: any = {}) {
      const activeEmail = normalizeAccountEmail(payload.email || state.activeEmail);
      const activeAccount = state.accounts.find((account: AccountInfo) =>
        normalizeAccountEmail(account.email) === activeEmail
      );
      const isAdviser = !!(payload.isAdviser || payload.is_adviser || (activeAccount && activeAccount.isAdviser));

      if (isAdviser && activeEmail) {
        removeStoredAccountSwitcherAccount(activeEmail);
        commit('removeAccount', activeEmail);
        if (normalizeAccountEmail(state.activeEmail) === activeEmail) {
          commit('setActiveEmail', '');
        }

        if (state.accounts.length === 0) {
          dispatch('clear');
          return;
        }

        commit('setIsMultiAccountUser', true);
        dispatch('persist');
        return;
      }

      dispatch('clear');
    },
  },

  getters: {
    /**
     * Check if an email contains a + (plus addressing)
     */
    isPlusEmail: () => (email: string): boolean => {
      if (!email) return false;
      const localPart = email.split('@')[0];
      return localPart.includes('+');
    },

    /**
     * Extract base email without the + suffix
     * e.g., tareef+acme@happily.ai -> tareef@happily.ai
     */
    extractBaseEmail: () => (email: string): string => {
      if (!email) return '';
      const [localPart, domain] = email.toLowerCase().split('@');
      const baseLocal = localPart.split('+')[0];
      return `${baseLocal}@${domain}`;
    },

    /**
     * Extract the company identifier from plus email
     * e.g., tareef+acme@happily.ai -> acme
     */
    extractCompanyTag: () => (email: string): string => {
      if (!email) return '';
      const localPart = email.split('@')[0];
      const parts = localPart.split('+');
      return parts.length > 1 ? parts[1] : '';
    },

    /**
     * Generate two-letter initials from company name
     */
    generateInitials: () => (companyName: string): string => {
      if (!companyName) return '??';
      
      const words = companyName.trim().split(/\s+/);
      if (words.length >= 2) {
        return (words[0][0] + words[1][0]).toUpperCase();
      }
      return companyName.substring(0, 2).toUpperCase();
    },

    /**
     * Get a consistent color for an email (deterministic)
     */
    getColorForEmail: () => (email: string): string => {
      if (!email) return AVATAR_COLORS[0];
      
      // Simple hash of email to pick a color
      let hash = 0;
      for (let i = 0; i < email.length; i++) {
        hash = ((hash << 5) - hash) + email.charCodeAt(i);
        hash = hash & hash; // Convert to 32-bit integer
      }
      
      return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
    },

    /**
     * Get accounts sorted alphabetically by company name
     */
    sortedAccounts: (state: AccountSwitcherState): AccountInfo[] => {
      return [...state.accounts].sort((a, b) => 
        a.companyName.toLowerCase().localeCompare(b.companyName.toLowerCase())
      );
    },

    /**
     * Get other accounts (not the active one)
     */
    otherAccounts: (state: AccountSwitcherState): AccountInfo[] => {
      return state.accounts.filter(a => a.email !== state.activeEmail);
    },

    /**
     * Get the active account info
     */
    activeAccount: (state: AccountSwitcherState): AccountInfo | undefined => {
      return state.accounts.find(a => a.email === state.activeEmail);
    },

    /**
     * Check if user has multiple accounts
     */
    hasMultipleAccounts: (state: AccountSwitcherState): boolean => {
      return state.accounts.length > 1;
    },

    /**
     * Check if account rail should be shown
     * Show if user is a multi-account user (has + email) AND has at least one account
     * This allows them to always add more accounts via the + button
     */
    shouldShowRail: (state: AccountSwitcherState): boolean => {
      return state.isMultiAccountUser && state.accounts.length >= 1;
    },
  },
};
